import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
  Fade,
  useTheme
} from '@mui/material';
import { 
  Thermostat, 
  Cloud, 
  Map, 
  Analytics,
  Security,
  Api,
  TrendingUp,
  Public
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ icon, title, description }) => (
  <Card sx={{ 
    height: '100%', 
    transition: 'all 0.3s ease',
    background: 'linear-gradient(145deg, rgba(26, 26, 46, 0.8) 0%, rgba(22, 33, 62, 0.8) 100%)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    backdropFilter: 'blur(20px)',
    '&:hover': { 
      transform: 'translateY(-8px)',
      borderColor: 'rgba(0, 212, 255, 0.3)',
      boxShadow: '0 12px 40px -12px rgba(0, 212, 255, 0.2)',
    } 
  }}>
    <CardContent sx={{ textAlign: 'center', p: 4 }}>
      <Box sx={{ 
        color: '#00d4ff', 
        mb: 3,
        display: 'flex',
        justifyContent: 'center',
        '& > *': {
          filter: 'drop-shadow(0 0 8px rgba(0, 212, 255, 0.3))'
        }
      }}>
        {icon}
      </Box>
      <Typography variant="h6" gutterBottom sx={{ 
        fontWeight: 600, 
        color: '#ffffff',
        mb: 2 
      }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ 
        color: 'rgba(255, 255, 255, 0.7)',
        lineHeight: 1.6 
      }}>
        {description}
      </Typography>
    </CardContent>
  </Card>
);

interface LandingPageProps {
  onLogin?: () => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onLogin }) => {
  const theme = useTheme();

  const features = [
    {
      icon: <Thermostat sx={{ fontSize: 48 }} />,
      title: "Real-Time Monitoring",
      description: "Monitor temperature, humidity, air quality, and weather conditions in real-time across multiple locations."
    },
    {
      icon: <Cloud sx={{ fontSize: 48 }} />,
      title: "Multi-Protocol Support",
      description: "Ingest data from various protocols including HTTP, MQTT, and CoAP for comprehensive sensor integration."
    },
    {
      icon: <Map sx={{ fontSize: 48 }} />,
      title: "Geospatial Analytics",
      description: "Advanced mapping and location-based analysis with PostGIS integration for spatial data insights."
    },
    {
      icon: <Analytics sx={{ fontSize: 48 }} />,
      title: "Time Series Analytics",
      description: "Powerful time series data analysis with TimescaleDB for historical trends and forecasting."
    },
    {
      icon: <Security sx={{ fontSize: 48 }} />,
      title: "Enterprise Security",
      description: "Keycloak-based authentication and authorization for secure access to environmental data."
    },
    {
      icon: <Api sx={{ fontSize: 48 }} />,
      title: "RESTful API",
      description: "Comprehensive API documentation with Swagger/OpenAPI for easy integration and development."
    }
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh',
      position: 'relative',
      backgroundImage: 'url(/background.jpg)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.85) 0%, rgba(13, 13, 31, 0.9) 50%, rgba(26, 26, 46, 0.85) 100%)',
        zIndex: 1,
      },
      '& > *': {
        position: 'relative',
        zIndex: 2,
      }
    }}>
      {/* Navigation Header - Hidden (using DESP header instead) */}

      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Fade in timeout={1000}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            {/* Big Logo */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center',
              alignItems: 'center',
              mb: 4,
              p: 2,
            }}>
              <img 
                src="/logo-white.svg" 
                alt="SenCore Logo" 
                style={{ height: '120px', width: 'auto' }}
              />
            </Box>
            
            {/* Motto below logo */}
            <Typography 
              variant="h1" 
              sx={{ 
                fontSize: { xs: '2.5rem', md: '4rem' },
                fontWeight: 700,
                mb: 4,
                background: 'linear-gradient(135deg, #00d4ff 0%, #ff6b6b 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                letterSpacing: '-0.02em',
                textShadow: '0 0 30px rgba(0, 212, 255, 0.2), 0 0 60px rgba(255, 107, 107, 0.15)',
                filter: 'drop-shadow(0 2px 6px rgba(0, 0, 0, 0.3))',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.08) 0%, rgba(255, 107, 107, 0.08) 100%)',
                  borderRadius: 2,
                  filter: 'blur(15px)',
                  zIndex: -1,
                },
              }}
            >
              Environmental Monitoring
              <br />
              Made Simple
            </Typography>
            
            <Typography 
              variant="h5" 
              sx={{ 
                mb: 4, 
                maxWidth: '800px', 
                mx: 'auto',
                color: 'rgba(255, 255, 255, 0.95)',
                lineHeight: 1.6,
                fontWeight: 400,
                textShadow: '0 2px 8px rgba(0, 0, 0, 0.7)',
                filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5))',
              }}
            >
              Comprehensive sensor monitoring platform across Europe with real-time data ingestion, 
              advanced analytics, and powerful visualization tools for environmental intelligence.
            </Typography>
            
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
              <Button
                onClick={onLogin}
                variant="contained"
                size="large"
                sx={{
                  px: 6,
                  py: 2.5,
                  fontSize: '1.5rem',
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #00d4ff 0%, #0099cc 100%)',
                  boxShadow: '0 8px 25px -8px rgba(0, 212, 255, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #33dcff 0%, #00b8e6 100%)',
                    boxShadow: '0 12px 35px -8px rgba(0, 212, 255, 0.5)',
                  }
                }}
              >
                Start Monitoring
              </Button>
            </Box>
          </Box>
        </Fade>

        {/* Stats Section */}
        <Fade in timeout={1500}>
          <Grid container spacing={4} sx={{ mb: 8 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h3" color="primary.main" sx={{ fontWeight: 'bold', mb: 1 }}>
                  500+
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Stations
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h3" color="primary.main" sx={{ fontWeight: 'bold', mb: 1 }}>
                  1.8K
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Data Points/sec
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h3" color="primary.main" sx={{ fontWeight: 'bold', mb: 1 }}>
                  15+
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Countries
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h3" color="primary.main" sx={{ fontWeight: 'bold', mb: 1 }}>
                  99.9%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Uptime
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Fade>

        {/* Features Section */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" align="center" gutterBottom sx={{ 
            mb: 6,
            fontWeight: 700,
            background: 'linear-gradient(135deg, #00d4ff 0%, #ff6b6b 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
          }}>
            Powerful Features
          </Typography>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Fade in timeout={1000 + index * 200}>
                  <div>
                    <FeatureCard {...feature} />
                  </div>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Call to Action */}
      </Container>

      {/* Footer */}
      <Box sx={{
        background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.8) 0%, rgba(13, 13, 31, 0.8) 100%)',
        backdropFilter: 'blur(20px)',
        py: 6,
        mt: 8,
        borderTop: '1px solid rgba(0, 212, 255, 0.2)'
      }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <img
                src="/logo-white.svg"
                alt="SenCore Logo"
                style={{ height: '40px' }}
              />
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: '0.875rem'
              }}
            >
              Copyright © 2025 Neuralio AI, All rights reserved
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;